import { t } from "@lingui/macro";
import { Check, Upload, X } from "@phosphor-icons/react";
import { PDFParser } from "@stylingcv/parser";
import { AnimatePresence, motion } from "framer-motion";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { createId } from "@paralleldrive/cuid2";

import { ENV_TOKEN_COSTS } from "@/client/config/env-token-config";
import { useTokenModal } from "@/client/contexts/token-modal-context";
import { useAuthStore } from "@/client/stores/auth";
import { useTokenStore } from "@/client/stores/token";
import {
  isInsufficientTokensMessage,
  parseNestErrorMessage,
  parseRequiredTokensFromMessage,
} from "@/client/utils/parse-ai-gateway-error";

type UploadResumeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  language: string;
};

type PostUploadChoice = "interview" | "builder" | null;

export const UploadResumeModal = ({ isOpen, onClose, language }: UploadResumeModalProps) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showTokenModal } = useTokenModal();
  const isLoggedIn = useAuthStore((s) => !!s.user);
  const fetchTokenBalance = useTokenStore((s) => s.fetchTokenBalance);
  const extractPdfCost = ENV_TOKEN_COSTS.EXTRACT_PDF_RESUME;
  
  // Upload states
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedResumeData, setUploadedResumeData] = useState<any>(null);
  const [showChoices, setShowChoices] = useState(false);

  // Handle PDF upload
  const handlePdfUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      setUploadError(t`Please upload a PDF file.`);
      return;
    }

    setIsUploading(true);
    setUploadedFileName(file.name);
    setUploadError(null);

    try {
      // 1. Extract Text locally
      const parser = new PDFParser();
      const pdfData = await parser.readFile(file);
      const text = pdfData.text;

      // 2. Send to AI Gateway for Structure (credentials so guestId cookie is sent)
      const response = await fetch("/api/ai/resume/parse-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ pdfText: text, language }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = parseNestErrorMessage(payload);

        if (message && isInsufficientTokensMessage(message)) {
          const required = parseRequiredTokensFromMessage(message) ?? extractPdfCost;
          await fetchTokenBalance(isLoggedIn, true);
          const current = useTokenStore.getState().tokenBalance ?? 0;
          showTokenModal({
            requiredTokens: required,
            currentTokens: current,
            featureName: t`PDF resume import`,
            onNavigation: onClose,
          });
          setUploadedFileName(null);
          return;
        }

        if (message.includes("Guest session not found")) {
          setUploadError(
            t`Your session could not be verified. Refresh the page and try again.`,
          );
          setUploadedFileName(null);
          return;
        }

        setUploadError(
          message || t`Failed to parse PDF. Please try another file.`,
        );
        setUploadedFileName(null);
        return;
      }

      const structuredData = await response.json();
      void fetchTokenBalance(isLoggedIn, true);
      setUploadedResumeData(structuredData);
      setShowChoices(true);
    } catch (error) {
      console.error("PDF parsing error:", error);
      setUploadError(t`Failed to parse PDF. Please try another file.`);
      setUploadedFileName(null);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle choice selection
  const handleChoice = async (choice: PostUploadChoice) => {
    if (!uploadedResumeData || !choice) return;

    if (choice === "interview") {
      onClose();
      // Navigate to AI interview with resume data
      navigate("/interview", {
        state: {
          resumeData: uploadedResumeData,
          source: "home-upload",
        },
      });
    } else if (choice === "builder") {
      onClose();
      try {
        // Transform data to proper Stylion format
        const formattedData = ensureStylingCVFormat(uploadedResumeData);

        // Try authenticated import first
        const response = await fetch("/api/resume/import", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ data: formattedData }),
        });

        if (response.ok) {
          const result = await response.json();
          const resumeId = result.id || result.slug;
          if (resumeId) {
            navigate(`/builder/${resumeId}`);
            return;
          }
        }

        // Fallback to guest import
        const guestResponse = await fetch("/api/guest/resume/import", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ data: formattedData }),
        });

        if (guestResponse.ok) {
          const result = await guestResponse.json();
          const resumeId = result.id || result.slug;
          if (resumeId) {
            navigate(`/builder/guest/${resumeId}`);
            return;
          }
        }

        // Last fallback
        navigate("/builder/try");
      } catch (error) {
        console.error("Error importing resume:", error);
        navigate("/builder/try");
      }
    }
  };

  // Reset state when modal closes
  const handleClose = () => {
    setIsUploading(false);
    setUploadedFileName(null);
    setUploadError(null);
    setUploadedResumeData(null);
    setShowChoices(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
        style={{ backgroundColor: "rgba(10, 10, 10, 0.95)", backdropFilter: "blur(12px)" }}
      >
        {/* Close button - above card so it's always visible */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-4 top-4 z-[100000] rounded-full border border-white/20 bg-white/10 p-2.5 text-white transition-all hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
          aria-label="Close"
        >
          <X size={22} />
        </button>

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative z-0 w-full max-w-lg overflow-hidden rounded-3xl border border-border bg-[#1a1a1a] p-8 shadow-2xl"
        >
          {/* Glow effects */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-1/4 top-0 size-64 rounded-full bg-cyan-500/10 blur-3xl" />
            <div className="absolute -right-1/4 bottom-0 size-64 rounded-full bg-emerald-500/10 blur-3xl" />
          </div>

          <div className="relative z-10">
            {!showChoices ? (
              <>
                {/* Upload Section */}
                <div className="mb-6 text-center">
                  <h2 className="mb-2 text-xl font-bold text-white">
                    {t`Upload Your Resume`}
                  </h2>
                  <p className="text-sm text-muted-foreground/80">
                    {t`We'll scan your PDF and extract all the information`}
                  </p>
                  <p className="mt-2 text-xs font-medium text-cyan-400/90">
                    {t`Cost:`} {extractPdfCost} {t`tokens`}
                  </p>
                </div>

                {/* Upload Dropzone */}
                <div className="relative">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    className="absolute inset-0 z-10 size-full cursor-pointer opacity-0"
                    disabled={isUploading}
                    onChange={handlePdfUpload}
                  />
                  <div
                    className={`w-full rounded-2xl border-2 border-dashed p-8 transition-all ${
                      uploadedFileName
                        ? "border-emerald-500/50 bg-emerald-500/10"
                        : "border-white/20 hover:border-cyan-400/50 hover:bg-white/5"
                    }`}
                  >
                    <div className="flex flex-col items-center justify-center gap-4">
                      {isUploading ? (
                        <UploadingAnimation fileName={uploadedFileName} />
                      ) : uploadedFileName ? (
                        <>
                          <div className="rounded-full bg-emerald-500/20 p-3">
                            <Check className="size-8 text-emerald-400" weight="bold" />
                          </div>
                          <div className="text-center">
                            <p className="text-sm font-medium text-emerald-400">
                              {uploadedFileName}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground/70">
                              {t`Resume uploaded successfully`}
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="rounded-full bg-white/5 p-4 ring-1 ring-white/10">
                            <Upload className="size-10 text-muted-foreground/70" />
                          </div>
                          <div className="text-center">
                            <p className="text-base font-medium text-muted-foreground">
                              {t`Drop your PDF here`}
                            </p>
                            <p className="mt-1 text-sm text-muted-foreground/70">
                              {t`or click to browse`}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {uploadError && (
                  <p className="mt-4 text-center text-sm text-red-400">{uploadError}</p>
                )}

                {/* Info text */}
                <div className="mt-6 rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
                  <p className="text-center text-xs text-muted-foreground/80">
                    {t`After upload, you can refine your resume via AI interview or go directly to the builder`}
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Post-Upload Choices */}
                <div className="mb-6 text-center">
                  <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-2xl bg-emerald-500/20">
                    <Check className="size-8 text-emerald-400" weight="bold" />
                  </div>
                  <h2 className="mb-2 text-xl font-bold text-white">
                    {t`Resume Parsed Successfully!`}
                  </h2>
                  <p className="text-sm text-muted-foreground/80">
                    {t`What would you like to do next?`}
                  </p>
                </div>

                <div className="space-y-3">
                  {/* Interview Option */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleChoice("interview")}
                    className="group flex w-full items-center gap-4 rounded-2xl border border-violet-500/30 bg-violet-500/10 p-4 text-left transition-all hover:border-violet-500/50 hover:bg-violet-500/20"
                  >
                    <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-violet-500/20 text-xl">
                      💬
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-white">{t`Verify with AI Interview`}</p>
                      <p className="text-xs text-muted-foreground/80">
                        {t`Refine and verify your details with AI`}
                      </p>
                    </div>
                    <span className="rounded-full bg-violet-500/20 px-2 py-0.5 text-[10px] font-bold text-violet-400">
                      {t`Recommended`}
                    </span>
                  </motion.button>

                  {/* Builder Option */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleChoice("builder")}
                    className="group flex w-full items-center gap-4 rounded-2xl border border-cyan-500/30 bg-cyan-500/10 p-4 text-left transition-all hover:border-cyan-500/50 hover:bg-cyan-500/20"
                  >
                    <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-cyan-500/20 text-xl">
                      ✏️
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-white">{t`Go to Builder`}</p>
                      <p className="text-xs text-muted-foreground/80">
                        {t`Edit and customize in the builder`}
                      </p>
                    </div>
                    <span className="rounded-full bg-cyan-500/20 px-2 py-0.5 text-[10px] font-bold text-cyan-400">
                      {t`Quick`}
                    </span>
                  </motion.button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Uploading Animation Component
const UploadingAnimation = ({ fileName }: { fileName: string | null }) => (
  <div className="flex flex-col items-center justify-center py-4">
    {/* Document scanning animation */}
    <div className="relative mb-6 h-48 w-36">
      {/* Document shadow */}
      <div className="absolute inset-0 rotate-2 scale-95 rounded-lg bg-white/5" />

      {/* Document */}
      <div className="relative size-full overflow-hidden rounded-lg border border-border bg-[#252525]">
        {/* Document content placeholder lines */}
        <div className="space-y-2 p-4">
          {/* Header placeholder */}
          <div className="mb-3 flex items-center gap-2">
            <div className="size-8 rounded-lg bg-cyan-500/20" />
            <div className="flex-1 space-y-1">
              <div className="h-2.5 w-3/4 rounded bg-white/10" />
              <div className="h-2 w-1/2 rounded bg-white/5" />
            </div>
          </div>

          {/* Content placeholder lines */}
          <div className="h-2 w-full rounded bg-white/10" />
          <div className="h-2 w-11/12 rounded bg-white/10" />
          <div className="h-2 w-4/5 rounded bg-white/10" />
          
          <div className="pt-2 space-y-1">
            <div className="h-2 w-1/3 rounded bg-cyan-500/20" />
            <div className="h-2 w-full rounded bg-white/10" />
            <div className="h-2 w-5/6 rounded bg-white/10" />
          </div>
        </div>

        {/* Animated scan line */}
        <motion.div
          className="absolute inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
          style={{ boxShadow: "0 0 15px 3px rgba(34, 211, 238, 0.4)" }}
          initial={{ top: 0 }}
          animate={{ top: ["0%", "100%", "0%"] }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      </div>
    </div>

    {/* Loading text */}
    <div className="mb-2 flex items-center gap-2">
      <motion.div
        className="size-4 rounded-full border-2 border-cyan-400 border-t-transparent"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      />
      <h3 className="text-sm font-medium text-white">
        {t`Reading your resume...`}
      </h3>
    </div>

    {fileName && (
      <p className="text-xs text-muted-foreground/70">{fileName}</p>
    )}
  </div>
);

/**
 * Ensure resume data matches Stylion schema format
 */
const ensureStylingCVFormat = (data: any) => {
  const basics = data.basics || {};
  const sections = data.sections || {};

  // Handle location - convert object to string if needed
  let locationString = "";
  if (typeof basics.location === "string") {
    locationString = basics.location;
  } else if (basics.location?.city) {
    locationString = [basics.location.city, basics.location.region, basics.location.country]
      .filter(Boolean)
      .join(", ");
  }

  // Check if data is in JSON Resume format
  const isJSONResumeFormat = data.work || data.education || data.skills;

  // Convert functions for JSON Resume format
  const convertWorkToExperience = (work: any[] = []) => {
    return work.map((job: any) => ({
      id: createId(),
      visible: true,
      company: job.company || "",
      position: job.position || "",
      location: job.location || "",
      date: `${job.startDate || ""} - ${job.endDate || "Present"}`,
      summary: job.summary || "",
      url: { label: "", href: job.url || "" },
    }));
  };

  const convertEducationItems = (education: any[] = []) => {
    return education.map((edu: any) => ({
      id: createId(),
      visible: true,
      institution: edu.institution || "",
      studyType: edu.studyType || "",
      area: edu.area || "",
      score: edu.score || "",
      date: `${edu.startDate || ""} - ${edu.endDate || ""}`,
      summary: edu.summary || "",
      url: { label: "", href: edu.url || "" },
    }));
  };

  const convertSkillsItems = (skills: any[] = []) => {
    return skills.map((skill: any) => ({
      id: createId(),
      visible: true,
      name: skill.name || "",
      description: skill.level || "",
      level: 3,
      keywords: skill.keywords || [],
    }));
  };

  // Build sections
  const experienceItems =
    isJSONResumeFormat && data.work?.length
      ? convertWorkToExperience(data.work)
      : sections.experience?.items || [];

  const educationItems =
    isJSONResumeFormat && data.education?.length
      ? convertEducationItems(data.education)
      : sections.education?.items || [];

  const skillsItems =
    isJSONResumeFormat && data.skills?.length
      ? convertSkillsItems(data.skills)
      : sections.skills?.items || [];

  const summaryContent = isJSONResumeFormat
    ? basics.summary || ""
    : sections.summary?.content || "";

  return {
    basics: {
      name: basics.name || "Your Name",
      headline: basics.headline || basics.label || "",
      email: basics.email || "",
      phone: basics.phone || "",
      location: locationString || "",
      url: basics.url || { label: "", href: "" },
      customFields: basics.customFields || [],
      picture: basics.picture || {
        url: "",
        size: 64,
        aspectRatio: 1,
        borderRadius: 0,
        effects: { hidden: false, border: false, grayscale: false },
      },
    },
    sections: {
      summary: {
        name: "Summary",
        columns: 1,
        visible: true,
        id: "summary",
        content: summaryContent,
      },
      experience: {
        name: "Experience",
        columns: 1,
        visible: true,
        id: "experience",
        items: experienceItems,
      },
      education: {
        name: "Education",
        columns: 1,
        visible: true,
        id: "education",
        items: educationItems,
      },
      skills: {
        name: "Skills",
        columns: 1,
        visible: true,
        id: "skills",
        items: skillsItems,
      },
      languages: sections.languages || {
        name: "Languages",
        columns: 1,
        visible: true,
        id: "languages",
        items: [],
      },
      certifications: sections.certifications || {
        name: "Certifications",
        columns: 1,
        visible: true,
        id: "certifications",
        items: [],
      },
      projects: sections.projects || {
        name: "Projects",
        columns: 1,
        visible: true,
        id: "projects",
        items: [],
      },
      custom: sections.custom || {},
    },
    metadata: data.metadata || {
      template: "vibrant",
      layout: [
        [
          ["summary", "experience", "education", "projects", "certifications", "awards", "mostProudOf", "myMind"],
          ["skills", "languages", "profiles", "interests", "hobbies", "volunteer", "publications", "references", "signature"],
        ],
      ],
      css: { value: "", visible: false },
      page: { margin: 18, format: "a4", options: { breakLine: true, pageNumbers: true } },
      typography: {
        font: { family: "Inter", subset: "latin", variants: ["regular"], size: 14 },
        lineHeight: 1.5,
        hideIcons: false,
        underlineLinks: true,
      },
      theme: { background: "#ffffff", text: "#000000", primary: "#0ea5e9" },
      notes: "",
    },
  };
};

